import React from 'react';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';
import { getToFixed } from 'helpers/format';
// @ts-ignore
import variables from 'styles/_variables.scss';
import './progressCircle.styles.scss';

const STROKE_WIDTH = 4;

export interface ProgressCircleProps {
	wrapperClass?: string;
	total?: number;
	used?: number | null;
	size?: number;
	addedText?: boolean;
	value?: number;
	percents?: boolean;
	revertColor?: boolean;
	fillAll?: boolean;
	useAntiClockwise?: boolean;
	strokeWidth?: number;
	customColor?: string;
	customLabel?: string | JSX.Element;
	textWrapperClass?: string;
	textContent?: string;
	type?: 'cards' | undefined;
}

export const ProgressCircle = ({
	wrapperClass = '',
	total = 0,
	used,
	addedText,
	percents = true,
	fillAll = true,
	revertColor,
	useAntiClockwise,
	strokeWidth = STROKE_WIDTH,
	customColor,
	customLabel,
	textWrapperClass,
	textContent,
	type,
}: ProgressCircleProps) => {
	const { t } = useTranslation('results');

	const value = getToFixed(((used || 0) / total) * 100);
	let dataLabel = 'Great';

	const r = 49 - strokeWidth / 2;
	const c = 2 * r * Math.PI;

	let offsetValue =
		revertColor && fillAll
			? 0
			: ((100 - (value > 100 ? 100 : value)) / 100) * c;

	if (isNaN(value)) {
		offsetValue = c;
	}

	if (value == 0) {
		offsetValue = 0;
	}

	let color = variables.positiveColor;

	if (revertColor) {
		if (used === null) {
			color = variables.borderColor;
		} else if (value >= 50 && value < 60) {
			color = variables.mediumColor;
			dataLabel = 'OK';
		} else if (value < 50) {
			color = variables.dangerColor;
			dataLabel = 'Poor';
		}
	} else {
		if (value > 50) {
			color = variables.mediumColor;
			dataLabel = 'OK';
		}
		if (value > 90) {
			color = variables.dangerColor;
			dataLabel = 'Poor';
		}
	}

	if (useAntiClockwise) {
		color = value > 0 ? variables.positiveColor : variables.dangerColor;
	}

	const defaultText =
		used !== null && used !== undefined
			? `${value}${isNaN(value) || !percents ? '' : '%'}`
			: t('no_applicable');

	let divClass = '';

	if (addedText) {
		divClass = 'progress-circle--textfull';
	}
	if (addedText && type === 'cards') {
		divClass = 'progress-circle--text-cards';
	}

	return (
		<div
			className={cn('progress-circle', wrapperClass, divClass)}
			data-pct={customLabel ? null : defaultText}
			data-label={textContent || dataLabel}
		>
			{customLabel && (
				<div className={cn(textWrapperClass, 'progress-circle__label')}>
					{customLabel}
				</div>
			)}
			<svg
				id="svg"
				viewBox="0 0 100 100"
				version="1.1"
				xmlns="http://www.w3.org/2000/svg"
			>
				<circle
					r={r}
					cx="50"
					cy="50"
					fill="transparent"
					strokeDasharray={c}
					strokeDashoffset="0"
					style={{
						strokeWidth: `${strokeWidth}px`,
					}}
				/>
				<circle
					r={r}
					cx="50"
					cy="50"
					fill="transparent"
					strokeDasharray={c}
					strokeDashoffset={offsetValue}
					style={{
						stroke: customColor || color,
						strokeWidth: `${strokeWidth}px`,
					}}
					transform="rotate(-90 50 50)"
					className="progress-circle__bar"
				/>
			</svg>
		</div>
	);
};
